import React from 'react';
import {Button, Container, Typography, Box, Paper, Grid, useMediaQuery} from "@mui/material";
import authSlice from "../../redux/reducers/authSlice";
import {styled} from "@mui/material/styles";
import {useAppDispatch} from "../../redux/hooks";
import config from "../../config";
import {theme} from "../../theme/theme";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import ApartmentIcon from '@mui/icons-material/Apartment';
import {useRouter} from '../../hooks/useRouter';

const FullPageContainer = styled(Container)({
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '24px',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
});

const ContentPaper = styled(Paper)({
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.12)',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '1200px',
});

const LeftPanel = styled(Box)(({theme}) => ({
  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
  color: 'white',
  padding: '48px 32px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    padding: '32px 24px',
  },
}));

const RightPanel = styled(Box)(({theme}) => ({
  padding: '48px 32px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'white',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    padding: '32px 24px',
  },
}));

const Logo = styled('img')({
  width: '180px',
  marginBottom: '24px',
});

const ActionButton = styled(Button)({
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
  },
});

const LoggedOutView: React.FC = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleLogin = () => {
    dispatch(authSlice.actions.setAuthState('login'));
    dispatch(authSlice.actions.openLoginModal());
  };

  const handleSignUp = () => {
    dispatch(authSlice.actions.setAuthState('create'));
    dispatch(authSlice.actions.openLoginModal());
  };

  const handleOpenBnbPortal = () => {
    router.push('/bnb-guest-portal');
  };

  return (
    <FullPageContainer maxWidth={false}>
      <ContentPaper elevation={0}>
        <Grid container sx={{ minHeight: '500px' }}>
          {!isMobile && (
            <Grid item xs={12} md={6}>
              <LeftPanel>
                <Logo src={config.whiteLogo} alt="WestProp Logo" />
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, marginBottom: 4, color: 'white' }}>
                  WestProp Residents Portal
                </Typography>
                <Typography variant="body1" sx={{ marginBottom: 4, opacity: 0.9, color: 'white' }}>
                  Your comprehensive digital hub for viewing property details, tracking levies, and topping up utilities - all in one secure platform.
                </Typography>
              </LeftPanel>
            </Grid>
          )}
          
          <Grid item xs={12} md={isMobile ? 12 : 6}>
            <RightPanel sx={{alignItems: 'center', justifyContent: 'center', textAlign: 'center', height: '100%'}}>
              {isMobile && (
                <Logo src={config.blueLogo} alt="WestProp Logo" />
              )}
              
              <Typography variant="h4" gutterBottom sx={{ 
                fontWeight: 700, 
                color: '#1976d2',
                marginBottom: 3,
                textAlign: 'center'
              }}>
                {isMobile ? 'WestProp Residents Portal' : 'Welcome Back'}
              </Typography>
              
              {isMobile && (
                <Typography variant="body1" sx={{ marginBottom: 4, textAlign: 'center' }}>
                  Your centralized portal for managing all WestProp operations.
                </Typography>
              )}
              
              <Typography variant="body1" sx={{ marginBottom: 4, textAlign: 'center' }}>
                Please log in to your account or sign up if you're new to the portal.
              </Typography>
              
              <Grid container spacing={2} sx={{ maxWidth: '400px' }}>
                <Grid item xs={12} sm={6}>
                  <ActionButton 
                    variant="contained" 
                    color="primary" 
                    fullWidth
                    startIcon={<LockOutlinedIcon />}
                    onClick={handleLogin}
                  >
                    Login
                  </ActionButton>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ActionButton 
                    variant="outlined" 
                    color="primary" 
                    fullWidth
                    startIcon={<PersonAddOutlinedIcon />}
                    onClick={handleSignUp}
                  >
                    Sign Up
                  </ActionButton>
                </Grid>
              </Grid>

              <Paper
                elevation={0}
                sx={{
                  mt: 3,
                  p: 2,
                  width: '100%',
                  maxWidth: 480,
                  border: '1px solid #e3ecf7',
                  borderRadius: 2,
                  backgroundColor: '#f7fbff',
                  textAlign: 'left',
                }}
              >
                <Typography variant="subtitle2" sx={{display: 'flex', alignItems: 'center', color: '#345678', fontWeight: 700}}>
                  <ApartmentIcon sx={{mr: 1, fontSize: 20}} />
                   Guest Access
                </Typography>
                <Typography variant="body2" sx={{mt: 0.5, color: 'text.secondary'}}>
                   Use your 6-digit access code to invite delivery or service visitors.
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  sx={{mt: 1, px: 0, fontWeight: 700, textTransform: 'none'}}
                  onClick={handleOpenBnbPortal}
                >
                  Open  Guest Portal
                </Button>
              </Paper>
              
              <Typography variant="caption" sx={{ marginTop: 4, color: 'text.secondary', textAlign: 'center' }}>
                © {new Date().getFullYear()} WestProp Holdings. All rights reserved.
              </Typography>
            </RightPanel>
          </Grid>
        </Grid>
      </ContentPaper>
    </FullPageContainer>
  );
}

export default LoggedOutView;